import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Input,
  Avatar,
  Button,
  message as antMessage,
  Spin,
  Upload,
  Card,
} from "antd";
import {
  PaperClipOutlined,
  SendOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import socketService from "../../services/socketService";
import * as messagesAPI from "../../api/messages";
import { AppContext } from "../../AppContext";

const Messages = () => {
  const { state } = useContext(AppContext);
  const location = useLocation();
  const currentUser = state.currentUser;
  const currentUserId = currentUser?.id;

  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typingUsers, setTypingUsers] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const previousRoomRef = useRef(null);

  useEffect(() => {
    socketService.onSidebarUpdate((data) => {
      const { roomId, lastMessage } = data;

      setRooms((prev) => {
        const updated = prev.map(
          (room) =>
            room.id === roomId
              ? {
                  ...room,
                  lastMessage: lastMessage, // fix undefined preview
                  lastMessageTime: lastMessage.createdAt,
                  unreadCount:
                    selectedRoom?.id === roomId
                      ? 0
                      : (room.unreadCount || 0) + 1, // add unread count only if not open
                  _hasNew: selectedRoom?.id !== roomId, // 🚨 highlight flag
                }
              : { ...room, _hasNew: false } // remove highlight if others
        );

        // Sort with latest on top (WhatsApp style)
        return updated.sort(
          (a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0)
        );
      });
    });

    return () => socketService.off("sidebarUpdate");
  }, [selectedRoom]);

  useEffect(() => {
    socketService.connect();
    fetchChatRooms();

    return () => {
      if (selectedRoom?.id) {
        socketService.leaveChatRoom({
          roomType: 1,
          roomId: selectedRoom.id,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentUserId) return;

    // 📩 When message is received anywhere
    const handleNewMessage = (data) => {
      const raw = data?.message || data;
      if (!raw) return;

      const msg = {
        id: raw.id,
        content: raw.content,
        createdAt: raw.createdAt || Date.now() / 1000,
        sender: raw.sender,
      };

      // 💬 If the room is OPEN → push to chat window
      if (selectedRoom?.id === data.roomId) {
        setMessages((prev) => [...prev, msg]);
      }

      // 🌍 ALWAYS UPDATE ROOM LIST (EVEN IF ROOM NOT OPEN)
      setRooms((prev) =>
        prev
          .map((room) =>
            room.id === data.roomId
              ? {
                  ...room,
                  lastMessage: msg,
                  lastMessageTime: msg.createdAt,
                  unreadCount:
                    selectedRoom?.id === room.id
                      ? 0 // reset if open
                      : (room.unreadCount || 0) + 1,
                  _hasNew: selectedRoom?.id !== room.id, // highlight
                }
              : room
          )
          .sort((a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0))
      );
    };

    const handleMessageSent = (data) => {
      if (selectedRoom && data?.roomId === selectedRoom?.id) {
        setMessages((prev) => {
          const filtered = prev.filter((msg) => !msg?.isOptimistic);
          const exists = filtered.some((msg) => msg?.id === data?.message?.id);
          if (exists) return prev;
          return [...filtered, data?.message];
        });
      }

      updateRoomLastMessage(data?.roomId, data?.message);
      setSending(false);
    };

    const handleTyping = (data) => {
      if (
        selectedRoom &&
        data.roomId === selectedRoom.id &&
        data.userId !== currentUserId
      ) {
        setTypingUsers([data.userId]);

        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        typingTimeoutRef.current = setTimeout(() => {
          setTypingUsers([]);
        }, 3000);
      }
    };

    socketService.onNewMessage(handleNewMessage);
    socketService.onMessageSent(handleMessageSent);
    socketService.onTyping(handleTyping);

    return () => {
      socketService.off("newMessage", handleNewMessage);
      socketService.off("messageSent", handleMessageSent);
      socketService.off("userTyping", handleTyping);
    };
  }, [selectedRoom, currentUserId]);

  useEffect(() => {
    if (selectedRoom && currentUserId) {
      if (previousRoomRef.current && previousRoomRef.current.id !== selectedRoom.id) {
        socketService.leaveChatRoom({
          roomType: 1,
          roomId: previousRoomRef.current.id,
        });
      }

      fetchChatHistory(selectedRoom.id, selectedRoom.patientInfo?.id);

      socketService.createChatRoom({
        roomType: 1,
        userId: currentUserId,
        patientId: selectedRoom.patientInfo?.id,
      });

      previousRoomRef.current = selectedRoom;
    }

    return () => {
      if (selectedRoom?.id) {
        socketService.leaveChatRoom({
          roomType: 1,
          roomId: selectedRoom.id,
        });
        previousRoomRef.current = null;
      }
    };
  }, [selectedRoom, currentUserId]);

  useEffect(() => {
    if (location.pathname !== "/messages" && selectedRoom?.id) {
      socketService.leaveChatRoom({
        roomType: 1,
        roomId: selectedRoom.id,
      });
      previousRoomRef.current = null;
    }
  }, [location.pathname, selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChatRooms = async () => {
    try {
      setLoading(true);
      const response = await messagesAPI.apiGetChatRooms();
      setRooms(response.data || []);
    } catch (error) {
      console.error("Error fetching chat rooms:", error);
      antMessage.error("Không thể tải cuộc hội thoại");
    } finally {
      setLoading(false);
    }
  };

  const fetchChatHistory = async (roomId, patientId) => {
    try {
      setLoadingMessages(true);
      const response = await messagesAPI.apiGetChatHistory(roomId, patientId);
      const messagesData = response?.data?.data || [];
      setMessages(messagesData.reverse());
    } catch (error) {
      console.error("Error fetching chat history:", error);
      antMessage.error("Không thể tải tin nhắn");
    } finally {
      setLoadingMessages(false);
    }
  };

  const updateRoomLastMessage = (roomId, message) => {
    setRooms((prev) =>
      prev.map((room) =>
        room.id === roomId
          ? {
              ...room,
              lastMessage: message?.content,
              lastMessageTime: message?.createdAt,
            }
          : room
      )
    );
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedRoom || !currentUserId) return;

    const messageContent = messageInput?.trim();
    const optimisticId = `temp-${Date.now()}`;

    const optimisticMessage = {
      id: optimisticId,
      content: messageContent,
      messageType: 1,
      file: null,
      createdAt: Math.floor(Date.now() / 1000),
      sender: {
        id: currentUserId,
        name: currentUser?.displayName || currentUser?.firstName,
        profileImage: currentUser?.profileImage,
        role: currentUser?.role,
      },
      isOptimistic: true,
    };

    try {
      setSending(true);
      setMessages((prev) => [...prev, optimisticMessage]);
      setMessageInput("");

      socketService.sendMessage({
        userId: currentUserId,
        roomId: selectedRoom?.id,
        roomType: 1,
        patientId: selectedRoom?.patientInfo?.id,
        messageType: 1,
        content: messageContent,
      });

      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === optimisticId ? { ...msg, isOptimistic: false } : msg
          )
        );
        setSending(false);
      }, 5000);
    } catch (error) {
      console.error("Error sending message:", error);
      antMessage.error("Không thể gửi tin nhắn");
      setMessages((prev) => prev.filter((msg) => msg.id !== optimisticId));
      setSending(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!selectedRoom || !currentUserId) {
      antMessage.error("Vui lòng chọn cuộc trò chuyện trước");
      return false;
    }

    try {
      antMessage.loading("Đang tải tệp...", 0);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("roomId", selectedRoom.id);
      formData.append("patientId", selectedRoom.patientInfo?.id);

      const response = await messagesAPI.apiUploadChatFile(formData);

      antMessage.destroy();
      antMessage.success("Tệp đã được tải lên thành công");

      const fileUrl = response?.data?.fileUrl;
      if (!fileUrl) {
        throw new Error("Máy chủ không trả về fileUrl");
      }

      const optimisticId = `temp-${Date.now()}`;

      const optimisticMessage = {
        id: optimisticId,
        content: null,
        messageType: 2,
        file: fileUrl,
        fileUrl: fileUrl,
        createdAt: Math.floor(Date.now() / 1000),
        sender: {
          id: currentUserId,
          name: currentUser?.displayName || currentUser?.firstName,
          profileImage: currentUser?.profileImage,
          role: currentUser?.role,
        },
        isOptimistic: true,
      };

      setMessages((prev) => [...prev, optimisticMessage]);

      socketService.sendMessage({
        userId: currentUserId,
        roomId: selectedRoom.id,
        roomType: 1,
        messageType: 2,
        fileUrl: fileUrl,
        content: null,
      });
    } catch (error) {
      antMessage.destroy();
      console.error("Upload error →", error);
      antMessage.error("Không thể tải tệp lên");
    }

    return false;
  };

  const handleTyping = () => {
    if (selectedRoom) {
      socketService.emitTyping(selectedRoom.id);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const filteredRooms = rooms.filter((room) =>
    room.patientInfo?.fullName
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const allottedPatients = filteredRooms.filter((room) => !room?.isPassive);
  const otherPatients = filteredRooms.filter((room) => room?.isPassive);

  const safeMessages = messages.filter((m) => m && m.content !== undefined);

  return (
    <div className="h-full bg-gray-50 p-6">
      {/* Header - OUTSIDE Card */}
      <div className="mb-6">
        <div className="flex">
          {selectedRoom && (
            <Button
              icon={<ArrowLeftOutlined />}
              type="text"
              onClick={() => setSelectedRoom(null)}
              className="mb-2 text-gray-600"
            />
          )}
          <h1 className="text-2xl font-semibold text-gray-900">Tin nhắn</h1>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Tất cả các thông tin cập nhật quan trọng của bạn đều được tập hợp tại một nơi
        </p>
      </div>

      {/* SINGLE Card with both sections */}
      <Card
        className="shadow-sm"
        bodyStyle={{
          padding: 0,
          height: "calc(100vh - 240px)",
          display: "flex",
        }}
      >
        {/* Left Section - Conversations List */}
        <div className="w-96 flex flex-col border-r border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Cuộc trò chuyện
            </h3>
            <Input
              placeholder="Tìm kiếm tin nhắn..."
              prefix={<SearchOutlined className="text-gray-400" />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Spin />
              </div>
            ) : (
              <>
                {allottedPatients.length > 0 && (
                  <div className="mb-4">
                    <p className="px-4 py-2 text-xs text-gray-500 tracking-wider font-medium">
                      ~ Bệnh nhân được phân bổ
                    </p>
                    {allottedPatients.map((room) => (
                      <div
                        key={room?.id}
                        className={`flex items-center gap-3 p-4 cursor-pointer transition-colors
                          ${
                            selectedRoom?.id === room?.id
                              ? "bg-purple-100 border-l-4 border-purple-600"
                              : room._hasNew
                              ? "bg-yellow-50 border-l-4 border-yellow-500" // ⭐ WhatsApp NEW highlight
                              : "hover:bg-gray-50"
                          }
                        `}
                        onClick={() => {
                          setSelectedRoom(room);

                          setRooms((prev) =>
                            prev.map((r) =>
                              r.id === room.id
                                ? { ...r, unreadCount: 0, _hasNew: false }
                                : r
                            )
                          );
                        }}
                      >
                        <Avatar size={48} src={room?.patientInfo?.profileImage}>
                          {room?.patientInfo?.fullName?.charAt(0).toUpperCase()}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-semibold text-gray-900 truncate text-sm">
                              {room?.patientInfo?.fullName}
                            </h4>
                            <span className="text-xs text-gray-500">
                              {formatTime(room?.lastMessageTime)}
                            </span>
                          </div>
                          <p
                            className={`text-xs truncate ${
                              room.unreadCount > 0
                                ? "font-semibold text-gray-900"
                                : "text-gray-500"
                            }`}
                          >
                            {room?.lastMessage?.content}
                          </p>
                        </div>
                        {room?.unreadCount > 0 && (
                          <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-xs text-white font-semibold">
                              {room?.unreadCount}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {otherPatients.length > 0 && (
                  <div>
                    <p className="px-4 py-2 text-xs text-gray-500 tracking-wider font-medium">
                      ~ Các bệnh nhân khác
                    </p>
                    {otherPatients.map((room) => (
                      <div
                        key={room?.id}
                        className={`flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedRoom?.id === room?.id
                            ? "bg-purple-50 border-l-4 border-purple-600"
                            : ""
                        }`}
                        onClick={() => setSelectedRoom(room)}
                      >
                        <Avatar size={48} src={room?.patientInfo?.profileImage}>
                          {room?.patientInfo?.fullName?.charAt(0).toUpperCase()}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-semibold text-gray-900 truncate text-sm">
                              {room?.patientInfo?.fullName}
                            </h4>
                            <span className="text-xs text-gray-500">
                              {formatTime(room?.lastMessageTime)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">
                            {room?.lastMessage?.content}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Section - Chat Area */}
        <div className="flex-1 flex flex-col">
          {selectedRoom ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex items-center gap-3">
                <Avatar size={48} src={selectedRoom.patientInfo?.profileImage}>
                  {selectedRoom.patientInfo?.fullName?.charAt(0).toUpperCase()}
                </Avatar>
                <div>
                  <h3 className="font-semibold text-gray-900 text-base">
                    {selectedRoom.patientInfo?.fullName}
                  </h3>
                  <p
                    className={`text-sm ${
                      selectedRoom.onlineStatus
                        ? "text-green-600"
                        : "text-gray-500"
                    }`}
                  >
                    {selectedRoom.onlineStatus ? "Online" : "Offline"}
                  </p>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-6 bg-purple-50">
                {loadingMessages ? (
                  <div className="flex justify-center items-center h-full">
                    <Spin size="large" />
                  </div>
                ) : (
                  <>
                    {safeMessages?.map((msg, index) => {
                      const senderId =
                        msg?.sender?.id || msg?.senderId || msg?.userId || null;

                      const isOwn = senderId === currentUserId;

                      const createdAt =
                        msg?.createdAt ||
                        msg?.timestamp ||
                        msg?.time ||
                        Math.floor(Date.now() / 1000);

                      const formattedTime = new Date(
                        createdAt * 1000
                      ).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      });

                      const prev = messages[index - 1];
                      const prevDate =
                        prev?.createdAt ||
                        prev?.timestamp ||
                        Math.floor(Date.now() / 1000);

                      const showDate =
                        !prev ||
                        new Date(createdAt * 1000).toDateString() !==
                          new Date(prevDate * 1000).toDateString();

                      return (
                        <React.Fragment key={msg.id || index}>
                          {showDate && (
                            <div className="text-center mb-4">
                              <span className="text-xs bg-white px-3 py-1 rounded-full">
                                {new Date(createdAt * 1000).toDateString()}
                              </span>
                            </div>
                          )}

                          <div
                            className={`flex mb-3 ${
                              isOwn ? "justify-end" : "justify-start"
                            }`}
                          >
                            <div
                              className={`p-3 max-w-md rounded-2xl ${
                                isOwn
                                  ? "bg-purple-600 text-white"
                                  : "bg-white text-black"
                              }`}
                            >
                              {msg?.messageType === 2 ? (
                                <img
                                  src={msg?.file}
                                  alt="attachment"
                                  className="max-w-[220px] w-full max-h-60 object-cover rounded-xl cursor-pointer"
                                  onClick={() =>
                                    window.open(msg?.file, "_blank")
                                  }
                                />
                              ) : (
                                <span className="whitespace-pre-line">
                                  {msg?.content}
                                </span>
                              )}

                              <div className="text-[10px] text-right mt-1 opacity-80">
                                {formattedTime}{" "}
                                {isOwn &&
                                  (msg?.seen
                                    ? "✓✓"
                                    : msg?.delivered
                                    ? "✓"
                                    : "•")}
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}

                    {typingUsers.length > 0 && (
                      <div className="flex items-end gap-2 mb-4">
                        <Avatar
                          size={32}
                          src={selectedRoom.patientInfo?.profileImage}
                        >
                          {selectedRoom.patientInfo?.fullName
                            ?.charAt(0)
                            .toUpperCase()}
                        </Avatar>
                        <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                            <span
                              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                              style={{ animationDelay: "0.1s" }}
                            ></span>
                            <span
                              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                              style={{ animationDelay: "0.2s" }}
                            ></span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <p className="text-xs text-gray-500 text-center mb-3">
                  Giờ hỗ trợ: 9 giờ sáng - 6 giờ chiều (Thứ Hai - Thứ Sáu). Nhóm của chúng tôi thường trả lời trong vòng 1 giờ.
                </p>

                {selectedRoom.isPassive ? (
                  <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">
                      Bạn không thể gửi tin nhắn cho bệnh nhân này. Cuộc trò chuyện này đang được một bác sĩ khác quản lý.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Nhập tin nhắn của bạn vào đây..."
                      value={messageInput}
                      onChange={(e) => {
                        setMessageInput(e.target.value);
                        handleTyping();
                      }}
                      onPressEnter={handleSendMessage}
                      size="large"
                      className="flex-1"
                      suffix={
                        <Upload
                          beforeUpload={handleFileUpload}
                          showUploadList={false}
                          accept="image/png, image/jpeg, image/jpg" // only images for now
                          multiple={false}
                        >
                          <Button type="text" icon={<PaperClipOutlined />} />
                        </Upload>
                      }
                    />
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      size="large"
                      onClick={handleSendMessage}
                      loading={sending}
                      disabled={!messageInput.trim() || sending}
                      className="bg-purple-600 hover:bg-purple-700 border-purple-600"
                    >
                      Gửi
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-purple-50">
              <div className="text-center">
                <svg
                  className="w-24 h-24 mx-auto text-gray-300 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                  />
                </svg>
                <p className="text-lg font-medium text-gray-700 mb-2">
                  Chọn một cuộc trò chuyện để bắt đầu nhắn tin
                </p>
                <p className="text-sm text-gray-500">
                  Chọn từ các cuộc trò chuyện hiện có của bạn ở bên trái
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Messages;
